// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Authentication from '@app/features/auth/state/Authentication';
import Messages from '@app/features/messaging/state/MessagingMessages';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {
	VAULT_CODE_TEMP,
	VAULT_DEFAULT_IDLE_MINUTES,
	VAULT_DEFAULT_TTL_MS,
	type VaultCode,
} from '@app/features/vault/constants';
import {getVaultEnvelopeExpiry, isVaultEnvelope} from '@app/features/vault/VaultCrypto';
import {makeAutoObservable, reaction} from 'mobx';

const STORAGE_KEY = 'yapnet.vault.v1';
const SWEEP_INTERVAL_MS = 60_000;

let sessionKey: CryptoKey | null = null;

export function getVaultSessionKey(): CryptoKey | null {
	return sessionKey;
}

export function setVaultSessionKey(key: CryptoKey | null): void {
	sessionKey = key;
}

interface PersistedVaultState {
	hiddenIds: Array<string>;
	tempIds: Array<string>;
	tempInviteIds: Array<string>;
	hiddenInviteIds: Array<string>;
	tempTtlMs: number;
	tempIdleMinutes: number;
	unseenHidden: Array<string>;
	unseenTemp: Array<string>;
	verifier?: string | null;
}

function loadPersistedState(): Partial<PersistedVaultState> {
	try {
		return AppStorage.getJSON<Partial<PersistedVaultState>>(STORAGE_KEY, {}) ?? {};
	} catch {
		return {};
	}
}

class Vault {
	tempUnlocked = false;
	hiddenUnlocked = false;
	hiddenIds: Array<string> = [];
	tempIds: Array<string> = [];
	tempInviteIds: Array<string> = [];
	hiddenInviteIds: Array<string> = [];
	tempTtlMs = VAULT_DEFAULT_TTL_MS;
	tempIdleMinutes = VAULT_DEFAULT_IDLE_MINUTES;
	unseenHidden: Array<string> = [];
	unseenTemp: Array<string> = [];
	verifier: string | null = null;
	sessionVersion = 0;
	pendingHideChannelId: string | null = null;
	pendingTempChannelId: string | null = null;
	pendingUnlockCode: VaultCode | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		const persisted = loadPersistedState();
		if (Array.isArray(persisted.hiddenIds)) this.hiddenIds = persisted.hiddenIds;
		if (Array.isArray(persisted.tempIds)) this.tempIds = persisted.tempIds;
		if (Array.isArray(persisted.tempInviteIds)) this.tempInviteIds = persisted.tempInviteIds;
		if (Array.isArray(persisted.hiddenInviteIds)) this.hiddenInviteIds = persisted.hiddenInviteIds;
		if (typeof persisted.tempTtlMs === 'number') this.tempTtlMs = persisted.tempTtlMs;
		if (typeof persisted.tempIdleMinutes === 'number') this.tempIdleMinutes = persisted.tempIdleMinutes;
		if (Array.isArray(persisted.unseenHidden)) this.unseenHidden = persisted.unseenHidden;
		if (Array.isArray(persisted.unseenTemp)) this.unseenTemp = persisted.unseenTemp;
		if (typeof persisted.verifier === 'string') this.verifier = persisted.verifier;
		// One-time migration: earlier defaults (5m TTL / 30m idle) silently deleted
		// messages; treat persisted values equal to the old defaults as unconfigured.
		if (this.tempTtlMs === 5 * 60_000) this.tempTtlMs = VAULT_DEFAULT_TTL_MS;
		if (this.tempIdleMinutes === 30) this.tempIdleMinutes = VAULT_DEFAULT_IDLE_MINUTES;
		if (typeof window !== 'undefined') {
			window.setInterval(() => this.sweep(), SWEEP_INTERVAL_MS);
		}
		reaction(
			() => SelectedChannel.currentChannelId,
			(channelId) => {
				if (channelId) this.markViewed(channelId);
			},
		);
	}

	private persist(): void {
		try {
			const state: PersistedVaultState = {
				hiddenIds: this.hiddenIds,
				tempIds: this.tempIds,
				tempInviteIds: this.tempInviteIds,
				hiddenInviteIds: this.hiddenInviteIds,
				tempTtlMs: this.tempTtlMs,
				tempIdleMinutes: this.tempIdleMinutes,
				unseenHidden: this.unseenHidden,
				unseenTemp: this.unseenTemp,
				verifier: this.verifier,
			};
			AppStorage.setJSON(STORAGE_KEY, state);
		} catch {
			// Storage may be unavailable; vault membership is best-effort across reloads.
		}
	}

	unlock(code: VaultCode): void {
		if (code === VAULT_CODE_TEMP) {
			this.tempUnlocked = true;
			this.unseenTemp = [];
			this.acceptTempInvites();
			if (this.pendingTempChannelId !== null) {
				const channelId = this.pendingTempChannelId;
				this.pendingTempChannelId = null;
				if (!this.isTempChannel(channelId)) this.toggleTemp(channelId);
			}
		} else {
			this.hiddenUnlocked = true;
			this.unseenHidden = [];
			this.acceptHiddenInvites();
			if (this.pendingHideChannelId !== null) {
				const channelId = this.pendingHideChannelId;
				this.pendingHideChannelId = null;
				if (!this.isInHidden(channelId)) this.toggleHidden(channelId);
			}
		}
		this.sessionVersion++;
		this.persist();
	}

	lockAll(): void {
		this.tempUnlocked = false;
		this.hiddenUnlocked = false;
		this.sessionVersion++;
		setVaultSessionKey(null);
	}

	isInHidden(channelId: string): boolean {
		return this.hiddenIds.includes(channelId);
	}

	isTempChannel(channelId: string): boolean {
		return this.tempIds.includes(channelId);
	}

	isTempInviteChannel(channelId: string): boolean {
		return this.tempInviteIds.includes(channelId);
	}

	isHiddenInviteChannel(channelId: string): boolean {
		return this.hiddenInviteIds.includes(channelId);
	}

	isVaultChannel(channelId: string): boolean {
		return (
			this.isInHidden(channelId) ||
			this.isTempChannel(channelId) ||
			this.isTempInviteChannel(channelId) ||
			this.isHiddenInviteChannel(channelId)
		);
	}

	isChannelUnlocked(channelId: string): boolean {
		const hiddenGate = this.isInHidden(channelId) || this.isHiddenInviteChannel(channelId) ? this.hiddenUnlocked : true;
		const tempGate = this.isTempChannel(channelId) || this.isTempInviteChannel(channelId) ? this.tempUnlocked : true;
		return hiddenGate && tempGate;
	}

	isLockedVaultChannel(channelId: string): boolean {
		return this.isVaultChannel(channelId) && !this.isChannelUnlocked(channelId);
	}

	get hasTempInvites(): boolean {
		return this.tempInviteIds.length > 0;
	}

	get hasHiddenInvites(): boolean {
		return this.hiddenInviteIds.length > 0;
	}

	get tempTabVisible(): boolean {
		return this.tempUnlocked || this.hasTempInvites;
	}

	get hiddenTabVisible(): boolean {
		return this.hiddenUnlocked || this.hasHiddenInvites;
	}

	get hasUnseenTempDot(): boolean {
		return this.unseenTemp.length > 0 || this.hasTempInvites;
	}

	get hasUnseenHiddenDot(): boolean {
		return this.unseenHidden.length > 0 || this.hasHiddenInvites;
	}

	addTempInvite(channelId: string): void {
		if (this.tempIds.includes(channelId) || this.tempInviteIds.includes(channelId)) return;
		if (this.tempUnlocked) {
			this.hiddenIds = this.hiddenIds.filter((id) => id !== channelId);
			this.unseenHidden = this.unseenHidden.filter((id) => id !== channelId);
			this.tempIds = [...this.tempIds, channelId];
			this.persist();
			return;
		}
		this.tempInviteIds = [...this.tempInviteIds, channelId];
		this.persist();
	}

	private acceptTempInvites(): void {
		if (this.tempInviteIds.length === 0) return;
		const merged = [...this.tempIds];
		for (const channelId of this.tempInviteIds) {
			if (!merged.includes(channelId)) merged.push(channelId);
		}
		this.tempIds = merged;
		this.tempInviteIds = [];
	}

	addHiddenInvite(channelId: string): void {
		if (this.hiddenIds.includes(channelId) || this.hiddenInviteIds.includes(channelId)) return;
		if (this.hiddenUnlocked) {
			this.tempIds = this.tempIds.filter((id) => id !== channelId);
			this.tempInviteIds = this.tempInviteIds.filter((id) => id !== channelId);
			this.unseenTemp = this.unseenTemp.filter((id) => id !== channelId);
			this.hiddenIds = [...this.hiddenIds, channelId];
			this.persist();
			return;
		}
		this.hiddenInviteIds = [...this.hiddenInviteIds, channelId];
		this.persist();
	}

	private acceptHiddenInvites(): void {
		if (this.hiddenInviteIds.length === 0) return;
		const merged = [...this.hiddenIds];
		for (const channelId of this.hiddenInviteIds) {
			if (!merged.includes(channelId)) merged.push(channelId);
		}
		this.hiddenIds = merged;
		this.hiddenInviteIds = [];
	}

	markViewed(channelId: string): void {
		const before = this.unseenHidden.length + this.unseenTemp.length;
		this.unseenHidden = this.unseenHidden.filter((id) => id !== channelId);
		this.unseenTemp = this.unseenTemp.filter((id) => id !== channelId);
		if (before !== this.unseenHidden.length + this.unseenTemp.length) this.persist();
	}

	setVerifier(envelope: string): void {
		this.verifier = envelope;
		this.persist();
	}

	toggleHidden(channelId: string): void {
		if (this.isInHidden(channelId)) {
			this.hiddenIds = this.hiddenIds.filter((id) => id !== channelId);
			this.unseenHidden = this.unseenHidden.filter((id) => id !== channelId);
		} else {
			this.hiddenIds = [...this.hiddenIds, channelId];
			this.tempIds = this.tempIds.filter((id) => id !== channelId);
			this.tempInviteIds = this.tempInviteIds.filter((id) => id !== channelId);
			this.hiddenInviteIds = this.hiddenInviteIds.filter((id) => id !== channelId);
			this.unseenTemp = this.unseenTemp.filter((id) => id !== channelId);
		}
		this.persist();
	}

	/** Hides the channel now, or queues it and returns true when the hidden vault must be unlocked first. */
	beginHideChannel(channelId: string): boolean {
		if (this.isInHidden(channelId)) {
			this.toggleHidden(channelId);
			return false;
		}
		if (!this.hiddenUnlocked) {
			this.pendingHideChannelId = channelId;
			return true;
		}
		this.toggleHidden(channelId);
		return false;
	}

	/** Marks the channel temporary now, or queues it and returns true when the temp vault must be unlocked first. */
	beginTempMark(channelId: string): boolean {
		if (this.isTempChannel(channelId)) {
			this.toggleTemp(channelId);
			return false;
		}
		if (!this.tempUnlocked) {
			this.pendingTempChannelId = channelId;
			return true;
		}
		this.toggleTemp(channelId);
		return false;
	}

	setPendingUnlockCode(code: VaultCode): void {
		this.pendingUnlockCode = code;
	}

	/**
	 * Drops a queued unlock intent (e.g. the unlock modal was cancelled).
	 * Pending channel actions for a still-locked vault are discarded so a later
	 * unlock does not silently hide/mark a channel the user abandoned.
	 * A successful unlock leaves the tab intent for the DM list to consume.
	 */
	clearPendingUnlock(code: VaultCode): void {
		if (code === VAULT_CODE_TEMP) {
			if (!this.tempUnlocked) {
				this.pendingTempChannelId = null;
				if (this.pendingUnlockCode === code) this.pendingUnlockCode = null;
			}
		} else if (!this.hiddenUnlocked) {
			this.pendingHideChannelId = null;
			if (this.pendingUnlockCode === code) this.pendingUnlockCode = null;
		}
	}

	consumePendingUnlockCode(code: VaultCode): void {
		if (this.pendingUnlockCode === code) this.pendingUnlockCode = null;
	}

	toggleTemp(channelId: string): void {
		if (this.isTempChannel(channelId)) {
			this.tempIds = this.tempIds.filter((id) => id !== channelId);
			this.unseenTemp = this.unseenTemp.filter((id) => id !== channelId);
		} else {
			this.tempIds = [...this.tempIds, channelId];
			this.hiddenIds = this.hiddenIds.filter((id) => id !== channelId);
			this.tempInviteIds = this.tempInviteIds.filter((id) => id !== channelId);
			this.unseenHidden = this.unseenHidden.filter((id) => id !== channelId);
		}
		this.persist();
	}

	setTempTtl(ms: number): void {
		this.tempTtlMs = ms;
		this.persist();
	}

	setTempIdleMinutes(minutes: number): void {
		this.tempIdleMinutes = minutes;
		this.persist();
	}

	noteIncoming(channelId: string, authorId: string): void {
		if (!this.isVaultChannel(channelId)) return;
		if (authorId === Authentication.currentUserId) return;
		if (this.isChannelUnlocked(channelId) && this.wasViewed(channelId)) return;
		if (this.isInHidden(channelId)) {
			if (!this.unseenHidden.includes(channelId)) this.unseenHidden = [...this.unseenHidden, channelId];
		}
		if (this.isTempChannel(channelId)) {
			if (!this.unseenTemp.includes(channelId)) this.unseenTemp = [...this.unseenTemp, channelId];
		}
		this.persist();
	}

	private wasViewed(channelId: string): boolean {
		return SelectedChannel.currentChannelId === channelId;
	}

	sweep(): void {
		if (this.tempIds.length === 0) return;
		const now = Date.now();
		const idleMs = this.tempIdleMinutes * 60_000;
		const currentUserId = Authentication.currentUserId;
		for (const channelId of this.tempIds) {
			const channelMessages = Messages.getMessages(channelId);
			if (!channelMessages) continue;
			const all = channelMessages.toArray();
			if (all.length === 0) continue;
			let newestTimestamp = 0;
			for (const message of all) {
				const timestamp = message.timestamp.getTime();
				if (timestamp > newestTimestamp) newestTimestamp = timestamp;
				if (message.author.id !== currentUserId) continue;
				const expiry = isVaultEnvelope(message.content) ? getVaultEnvelopeExpiry(message.content) : null;
				if (expiry !== null && now > expiry) {
					this.deleteMessage(channelId, message.id);
				}
			}
			if (idleMs > 0 && newestTimestamp > 0 && now - newestTimestamp > idleMs) {
				for (const message of all) {
					if (message.author.id !== currentUserId) continue;
					this.deleteMessage(channelId, message.id);
				}
			}
		}
	}

	private deleteMessage(channelId: string, messageId: string): void {
		void http.delete(Endpoints.CHANNEL_MESSAGE(channelId, messageId)).catch(() => {
			// Already deleted or not ours; the gateway delete event keeps state in sync.
		});
	}
}

export default new Vault();
